/**
 * Feature Flag Utilities
 *
 * Helper functions for working with feature flags and workspace preferences
 */

import { WorkspacePreferences } from '@/lib/graphql/types-and-hooks';
import { FeatureFlag } from './types';

/**
 * Parse workspace preferences JSON safely
 */
const parsePreferences = (preferences: string | null | undefined): Record<string, any> => {
  try {
    return JSON.parse(preferences || '{}');
  } catch (e) {
    console.error('Failed to parse workspace preferences:', e);
    return {};
  }
};

/**
 * Get the value of a feature flag from workspace preferences
 * Returns the default value if not found or if preferences are null
 */
export const getFeatureFlagValue = (
  flag: FeatureFlag,
  workspacePreferences: WorkspacePreferences | null | undefined
): boolean => {
  if (!workspacePreferences) {
    return flag.defaultValue;
  }

  const preferences = parsePreferences(workspacePreferences.preferences);

  // Check if the key exists in preferences
  if (Object.prototype.hasOwnProperty.call(preferences, flag.key)) {
    return Boolean(preferences[flag.key]);
  }

  // Return default value if not set
  return flag.defaultValue;
};

/**
 * Check if a feature is explicitly enabled in preferences
 * Returns true only if the key exists and is set to true
 */
export const isFeatureEnabled = (
  key: string,
  workspacePreferences: WorkspacePreferences | null | undefined
): boolean => {
  if (!workspacePreferences) {
    return false;
  }

  const preferences = parsePreferences(workspacePreferences.preferences);

  return Object.prototype.hasOwnProperty.call(preferences, key) && Boolean(preferences[key]);
};

/**
 * Check if a feature is explicitly disabled in preferences
 * Returns true only if the key exists and is set to false
 */
export const isFeatureDisabled = (
  key: string,
  workspacePreferences: WorkspacePreferences | null | undefined
): boolean => {
  if (!workspacePreferences) {
    return false;
  }

  const preferences = parsePreferences(workspacePreferences.preferences);

  return Object.prototype.hasOwnProperty.call(preferences, key) && !Boolean(preferences[key]);
};
